import { Fragment } from 'react'
import Link from 'next/link'
import ReactMarkdown from 'react-markdown'
import { DateTime } from 'luxon'

import Badge from 'components/Badge/Badge'
import LabelBadge from 'components/LabelBadge'
import { ProductBadgeEnum } from '../../../../../../__generated__/globalTypes'
import { ProductDetailsFragment } from './graphql/__generated__/ProductDetailsFragment'
import StockStatusBadge from '../StockStatusBadge'

import styles from './ProductDetails.module.css'

interface ProductDetailsProps {
  product: ProductDetailsFragment
  isPreview?: boolean
}

const ProductDetails = ({ product, isPreview = false }: ProductDetailsProps) => {
  const isStack = product.isStack
  const badge = product.badge
  const inStock = !product.outOfStock
  const stockOnHand = product.firstVariety?.stockOnHand
  const stockDueDate = product.stockDueDate ? DateTime.fromISO(product.stockDueDate) : null
  const allowBackorders = product.catalog.createBackorders

  const showStockStatusBadge = !isStack && !(inStock && badge === ProductBadgeEnum.SOLD_OUT)
  const showProductBadge = badge && !(!inStock && badge === ProductBadgeEnum.SOLD_OUT)

  const slugPath = product.productCategoryTaxonomy?.slugPath
  const fullPathArray = product.productCategoryTaxonomy?.fullPath?.split(' > ') || []

  const productCategoryLinks = fullPathArray.reduce((accumulator, current, index) => {
    accumulator[current] = slugPath?.slice(0, index + 1) || []
    return accumulator
  }, {} as Record<string, string[]>)

  return (
    <>
      <div className={styles.sellerDisplayNameContainer}>
        <Link href={`/${product.catalog.seller.slug}${isPreview ? '?preview=1' : ''}`}>
          <a className={styles.sellerDisplayName}>{product.catalog.seller.displayName}</a>
        </Link>
      </div>

      <div className={styles.productNameContainer}>
        <h1 className={styles.productName}>
          {product.catalog.seller.displayName} {product?.name}
        </h1>
      </div>

      {product.productCategoryTaxonomy && (
        <div className={styles.productCategoryContainer}>
          {productCategoryLinks &&
            Object.entries(productCategoryLinks).flatMap(([name, slugPath], index, array) => [
              <Fragment key={name}>
                <Link href={`/shop/c/${slugPath.join('/')}`}>
                  <a className={styles.productCategoryLink}>{name}</a>
                </Link>
              </Fragment>,
              index < array.length - 1 && (
                <span key={`${name}-separator`} className={styles.productCategorySeparator}>
                  ›
                </span>
              )
            ])}
        </div>
      )}

      <div className={styles.productBadgesContainer}>
        <div className={styles.productBadgesGroup}>
          {showProductBadge && <LabelBadge hasMargin id={ProductBadgeEnum[badge]} />}
          {showStockStatusBadge && (
            <StockStatusBadge
              inStock={inStock}
              allowBackorders={allowBackorders}
              stockDueDate={stockDueDate}
              stockOnHand={stockOnHand}
              badge={badge}
              hasMargin
              showIcon
            />
          )}
        </div>

        {product.brandValues.length > 0 && (
          <div className={styles.productBadgesGroup}>
            {product.brandValues.map(value => (
              <Badge hasMargin className={styles.productbadge} kind="brand" style="tinted" key={value.id}>
                {value.name}
              </Badge>
            ))}
          </div>
        )}
      </div>
      {product.brand && (
        <div className={styles.productBrandContainer}>
          <div className={styles.productBrand}>{product.brand}</div>
        </div>
      )}
      {product.extendedDescription && (
        <div className={styles.productDescriptionContainer}>
          <div className={styles.productDescription}>
            {/* TODO: Restrict markdown, use custom renderer for links with target="_blank", or just disallow links */}
            <ReactMarkdown>{product.extendedDescription}</ReactMarkdown>
          </div>
        </div>
      )}
      {product.customTags && (
        <ul className={styles.productCustomTags}>
          {product.customTags.map(tag => (
            <li key={tag.id}>
              <Link href={`/${product.catalog.seller.slug}/${product.catalog.slug}?customTags=${tag.name}`}>
                <a>{tag.name}</a>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

export default ProductDetails
